import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import type { BookSize } from '../../shared/api';
import { BookCover } from '../components/BookCover';
import { NewCategoryForm } from '../components/NewCategoryForm';
import { SizePicker } from '../components/SizePicker';
import { StatusBadge } from '../components/StatusBadge';
import { useCategories } from '../hooks/useCategories';
import { useResource } from '../hooks/useResource';
import { api, ApiError, errorMessage } from '../lib/api';
import { formatAuthors, formatDate, SOURCE_LABELS } from '../lib/format';

export function BookPage() {
  const { isbn = '' } = useParams();
  const navigate = useNavigate();
  const { data: book, error, loading, reload, setData } = useResource(
    `book:${isbn}`,
    (signal) => api.getBook(isbn, signal),
    { pollWhile: (current) => current.status === 'pending' },
  );
  const [busy, setBusy] = useState<'refreshing' | 'deleting' | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const { categories, create: createCategory } = useCategories();
  const [savingCategories, setSavingCategories] = useState(false);
  const [addingCategory, setAddingCategory] = useState(false);
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const [savingSize, setSavingSize] = useState(false);
  const [sizeError, setSizeError] = useState<string | null>(null);

  async function saveSize(size: BookSize | null) {
    setSavingSize(true);
    setSizeError(null);
    try {
      setData(await api.setBookSize(isbn, size));
    } catch (err) {
      setSizeError(errorMessage(err));
    } finally {
      setSavingSize(false);
    }
  }

  async function saveCategories(categoryIds: number[]) {
    setSavingCategories(true);
    setCategoryError(null);
    try {
      setData(await api.setBookCategories(isbn, categoryIds));
    } catch (err) {
      setCategoryError(errorMessage(err));
    } finally {
      setSavingCategories(false);
    }
  }

  function toggleCategory(categoryId: number) {
    if (!book) return;
    const current = book.categories.map((category) => category.id);
    void saveCategories(
      current.includes(categoryId) ? current.filter((id) => id !== categoryId) : [...current, categoryId],
    );
  }

  async function refresh() {
    setBusy('refreshing');
    setActionError(null);
    try {
      setData(await api.refreshBook(isbn));
    } catch (err) {
      setActionError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function remove() {
    if (!window.confirm(`Remove "${book?.title ?? isbn}" from your collection?`)) return;
    setBusy('deleting');
    setActionError(null);
    try {
      await api.deleteBook(isbn);
      navigate('/collection', { replace: true });
    } catch (err) {
      setActionError(errorMessage(err));
      setBusy(null);
    }
  }

  const backLink = (
    <Link className="back-link" to="/collection">
      ← Collection
    </Link>
  );

  if (!book) {
    if (loading) return <p className="page-status">Loading…</p>;
    const notFound = error instanceof ApiError && (error.status === 404 || error.code === 'INVALID_ISBN');
    return (
      <section className="page-status" role="alert">
        {backLink}
        <p className={notFound ? undefined : 'error-text'}>
          {notFound ? 'That book is not in your collection.' : errorMessage(error)}
        </p>
        {!notFound && (
          <button type="button" className="btn btn--primary" onClick={reload}>
            Try again
          </button>
        )}
      </section>
    );
  }

  const details: Array<[label: string, value: string | null]> = [
    ['Publisher', book.publishers.join(', ') || null],
    ['Published', book.publishDate],
    ['Pages', book.numberOfPages?.toString() ?? null],
    ['ISBN', book.isbn],
    ['Details from', book.source ? SOURCE_LABELS[book.source] : null],
    ['Added', formatDate(book.addedAt)],
  ];

  return (
    <article className="book-page">
      {backLink}
      <div className="book-page__layout">
        <BookCover src={book.coverLargeUrl ?? book.coverUrl} title={book.title} size="large" />
        <div className="book-page__info">
          <h1>{book.title ?? `ISBN ${book.isbn}`}</h1>
          {book.subtitle && <p className="subtitle">{book.subtitle}</p>}
          {book.status === 'complete' && <p className="authors">{formatAuthors(book.authors)}</p>}
          <StatusBadge status={book.status} />
          {book.status === 'error' && book.lastError && <p className="error-text">{book.lastError}</p>}
          {book.status === 'pending' && book.lastError && (
            <p className="muted">Last attempt failed ({book.lastError}); retrying automatically.</p>
          )}

          <dl className="details">
            {details
              .filter((entry): entry is [string, string] => entry[1] !== null)
              .map(([label, value]) => (
                <div key={label} className="details__row">
                  <dt>{label}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
          </dl>

          <section className="book-categories" aria-labelledby="book-categories-title">
            <h2 id="book-categories-title">Categories</h2>
            <div className="category-toggles">
              {(categories ?? book.categories).map((category) => {
                const selected = book.categories.some((c) => c.id === category.id);
                return (
                  <button
                    key={category.id}
                    type="button"
                    className={`category-toggle${selected ? ' is-selected' : ''}`}
                    aria-pressed={selected}
                    disabled={savingCategories}
                    onClick={() => toggleCategory(category.id)}
                  >
                    {selected && <span aria-hidden="true">✓ </span>}
                    {category.name}
                  </button>
                );
              })}
              {!addingCategory && (
                <button type="button" className="category-toggle category-toggle--new" onClick={() => setAddingCategory(true)}>
                  ＋ New
                </button>
              )}
            </div>
            {addingCategory && (
              <NewCategoryForm
                create={createCategory}
                autoFocus
                onCreated={(category) => {
                  setAddingCategory(false);
                  void saveCategories([...book.categories.map((c) => c.id), category.id]);
                }}
                onCancel={() => setAddingCategory(false)}
              />
            )}
            {categoryError && (
              <p className="error-text" role="alert">
                {categoryError}
              </p>
            )}
          </section>

          <section className="book-size" aria-labelledby="book-size-title">
            <h2 id="book-size-title">Size</h2>
            <SizePicker label="Height" selected={book.size} onSelect={(size) => void saveSize(size)} disabled={savingSize} />
            {sizeError && (
              <p className="error-text" role="alert">
                {sizeError}
              </p>
            )}
          </section>

          {book.subjects.length > 0 && (
            <section className="book-subjects" aria-labelledby="book-subjects-title">
              <h2 id="book-subjects-title">Subjects</h2>
              <ul className="subjects">
                {book.subjects.map((subject) => (
                  <li key={subject}>{subject}</li>
                ))}
              </ul>
            </section>
          )}

          {book.sourceUrl && book.source && (
            <p>
              <a href={book.sourceUrl} target="_blank" rel="noopener noreferrer">
                View on {SOURCE_LABELS[book.source]} ↗
              </a>
            </p>
          )}

          {actionError && (
            <p className="error-text" role="alert">
              {actionError}
            </p>
          )}
          <div className="actions">
            <button
              type="button"
              className="btn btn--secondary"
              onClick={() => void refresh()}
              disabled={busy !== null || book.status === 'pending'}
            >
              {busy === 'refreshing' ? 'Refreshing…' : 'Refresh details'}
            </button>
            <button type="button" className="btn btn--danger" onClick={() => void remove()} disabled={busy !== null}>
              {busy === 'deleting' ? 'Removing…' : 'Remove'}
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}
